import React, { useEffect, useMemo, useRef, useState } from "react";
import MobileFloorBar from "./MobileFloorBar";

const WEEKDAYS = ["H", "K", "Sze", "Cs", "P", "Szo", "V"];
const MONTHS = [
  "Januar", "Februar", "Marcius", "Aprilis", "Majus", "Junius",
  "Julius", "Augusztus", "Szeptember", "Oktober", "November", "December",
];
const SLOT_H = 48; // px per hour
const DEFAULT_SCROLL_HOUR = 6;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  x.setDate(x.getDate() + (day === 0 ? -6 : 1 - day));
  x.setHours(0, 0, 0, 0);
  return x;
}

function isoLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function planToMinutes(scheduled_time) {
  if (!scheduled_time) return null;
  const [h, m] = scheduled_time.split(":").map(Number);
  return h * 60 + (m || 0);
}

// Shared time grid scroll container – scrolls to DEFAULT_SCROLL_HOUR on mount/view change
function TimeGrid({ children, scrollKey }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = DEFAULT_SCROLL_HOUR * SLOT_H - 8;
    }
  }, [scrollKey]);
  return (
    <div ref={ref} className="flex-1 overflow-y-auto">
      {children}
    </div>
  );
}

// Day view time grid
function DayTimeGrid({ plans, onOpenMap, scrollKey }) {
  const withTime = plans.filter((p) => p.scheduled_time);
  const noTime = plans.filter((p) => !p.scheduled_time);

  return (
    <TimeGrid scrollKey={scrollKey}>
      {noTime.length > 0 && (
        <div className="px-2 py-1 bg-slate-50 border-b border-slate-200">
          <div className="text-[10px] text-slate-400 mb-1">Idopont nelkul</div>
          {noTime.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onOpenMap()}
              className="w-full text-left rounded border border-teal-200 bg-white px-2 py-1 mb-1 text-xs"
            >
              <span className="font-bold text-teal-700">{p.route_code || "Route"}</span>
              {" "}{p.route_name || p.location_name || ""}
            </button>
          ))}
        </div>
      )}
      <div className="relative" style={{ height: 24 * SLOT_H }}>
        {/* Hour lines */}
        {HOURS.map((h) => (
          <div
            key={h}
            className="absolute left-0 right-0 border-t border-slate-200 flex items-start"
            style={{ top: h * SLOT_H }}
          >
            <span className="text-[10px] text-slate-400 w-10 pl-1 leading-none pt-0.5 shrink-0">
              {String(h).padStart(2, "0")}:00
            </span>
          </div>
        ))}
        {/* Plans */}
        {withTime.map((p) => {
          const mins = planToMinutes(p.scheduled_time);
          const topPx = (mins / 60) * SLOT_H;
          const heightPx = Math.max(SLOT_H * 0.8, ((p.estimated_minutes || 60) / 60) * SLOT_H);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onOpenMap()}
              className="absolute left-11 right-2 rounded-md bg-teal-600/90 text-white text-left px-2 py-0.5 overflow-hidden"
              style={{ top: topPx + 1, height: heightPx - 2 }}
            >
              <div className="text-xs font-bold leading-tight">{p.route_code || "Route"}</div>
              <div className="text-[10px] leading-tight opacity-90 truncate">{p.route_name || p.location_name || ""}</div>
              <div className="text-[10px] opacity-75">{p.scheduled_time}</div>
            </button>
          );
        })}
      </div>
    </TimeGrid>
  );
}

// Week view time grid – 7 narrow columns
function WeekTimeGrid({ weekDays, plansByDate, selectedDate, onSelectDate, onOpenMap, scrollKey }) {
  return (
    <TimeGrid scrollKey={scrollKey}>
      {/* Day headers */}
      <div className="flex sticky top-0 bg-white z-10 border-b border-slate-200">
        <div className="w-10 shrink-0" />
        {weekDays.map((d) => {
          const iso = isoLocal(d);
          const isSelected = iso === selectedDate;
          const isToday = iso === new Date().toISOString().split("T")[0];
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelectDate(iso)}
              className={`flex-1 min-w-0 py-1 text-center text-[11px] font-semibold border-l border-slate-100 ${
                isSelected ? "bg-teal-50 text-teal-700" : "text-slate-700"
              }`}
            >
              <div>{WEEKDAYS[weekDays.indexOf(d)]}</div>
              <div
                className={`text-sm font-bold leading-tight ${
                  isToday ? "bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center mx-auto" : ""
                }`}
              >
                {d.getDate()}
              </div>
            </button>
          );
        })}
      </div>

      {/* Time grid body */}
      <div className="relative flex" style={{ height: 24 * SLOT_H }}>
        {/* Hour labels */}
        <div className="w-10 shrink-0 relative">
          {HOURS.map((h) => (
            <div
              key={h}
              className="absolute left-0 right-0 border-t border-slate-200"
              style={{ top: h * SLOT_H }}
            >
              <span className="text-[9px] text-slate-400 pl-0.5 leading-none pt-0.5 block">
                {String(h).padStart(2, "0")}:00
              </span>
            </div>
          ))}
        </div>

        {/* Day columns */}
        {weekDays.map((d) => {
          const iso = isoLocal(d);
          const dp = plansByDate[iso] || [];
          return (
            <div
              key={iso}
              className="flex-1 min-w-0 relative border-l border-slate-100"
              style={{ height: 24 * SLOT_H }}
            >
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-slate-100"
                  style={{ top: h * SLOT_H, height: SLOT_H }}
                />
              ))}
              {dp.map((p) => {
                const mins = planToMinutes(p.scheduled_time);
                if (mins === null) {
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { onSelectDate(iso); onOpenMap(iso); }}
                      className="absolute top-0.5 left-0.5 right-0.5 rounded bg-teal-500/80 text-white text-[9px] font-bold px-0.5 py-0.5 truncate"
                      style={{ height: SLOT_H * 0.7 }}
                    >
                      {p.route_code || "?"}
                    </button>
                  );
                }
                const topPx = (mins / 60) * SLOT_H;
                const heightPx = Math.max(SLOT_H * 0.65, ((p.estimated_minutes || 60) / 60) * SLOT_H);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { onSelectDate(iso); onOpenMap(iso); }}
                    className="absolute left-0.5 right-0.5 rounded bg-teal-600/90 text-white text-[9px] font-bold px-0.5 overflow-hidden"
                    style={{ top: topPx + 1, height: heightPx - 2 }}
                  >
                    {p.route_code || "?"}
                    <div className="text-[8px] opacity-80 font-normal">{p.scheduled_time}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </TimeGrid>
  );
}

const MobileCleanerCalendar = ({
  rootLocations,
  activeRootLocationId,
  setActiveRootLocationId,
  selectedDate,
  onSelectDate,
  onOpenMapForDate,
}) => {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => {
    const [y, m] = selectedDate.split("-").map(Number);
    return new Date(y, m - 1, 1);
  });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => {
    if (view === "month") {
      const start = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      return { from: isoLocal(start), to: isoLocal(end) };
    }
    if (view === "week") {
      const ws = startOfWeek(cursor);
      const we = new Date(ws);
      we.setDate(we.getDate() + 6);
      return { from: isoLocal(ws), to: isoLocal(we) };
    }
    return { from: selectedDate, to: selectedDate };
  }, [view, cursor, selectedDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `/api/my-cleaning-plans?date_from=${range.from}&date_to=${range.to}`,
          { credentials: "include" }
        );
        const data = await res.json();
        if (!cancelled && data.success) setPlans(data.plans || []);
      } catch (e) {
        if (!cancelled) setPlans([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const plansByDate = useMemo(() => {
    const map = {};
    plans.forEach((p) => {
      if (!p.plan_date) return;
      if (!map[p.plan_date]) map[p.plan_date] = [];
      map[p.plan_date].push(p);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) =>
        (a.scheduled_time || "99:99").localeCompare(b.scheduled_time || "99:99")
      )
    );
    return map;
  }, [plans]);

  const monthCells = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    const start = startOfWeek(first);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const weekDays = useMemo(() => {
    const ws = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(ws);
      d.setDate(ws.getDate() + i);
      return d;
    });
  }, [cursor]);

  const dayPlans = plansByDate[selectedDate] || [];

  const shiftPeriod = (delta) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else d.setDate(d.getDate() + delta);
    setCursor(d);
    if (view === "day") {
      const iso = isoLocal(d);
      onSelectDate(iso);
    }
  };

  const titleLabel = () => {
    if (view === "month") return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === "week") return `${isoLocal(weekDays[0])} – ${isoLocal(weekDays[6])}`;
    return selectedDate;
  };

  // scrollKey resets scroll position when view or period changes
  const scrollKey = `${view}-${range.from}`;

  return (
    <div className="flex flex-col h-full bg-slate-100">
      <MobileFloorBar
        rootLocations={rootLocations}
        activeRootLocationId={activeRootLocationId}
        setActiveRootLocationId={setActiveRootLocationId}
        showDateNav={false}
      />
      <div className="flex items-center gap-1 px-2 py-2 border-b border-slate-200 bg-white shrink-0">
        <button type="button" onClick={() => shiftPeriod(-1)} className="px-2 py-1 text-slate-600 text-lg">‹</button>
        <span className="flex-1 text-center text-xs font-semibold text-slate-800 truncate">{titleLabel()}</span>
        <button type="button" onClick={() => shiftPeriod(1)} className="px-2 py-1 text-slate-600 text-lg">›</button>
        <div className="flex rounded border border-slate-200 overflow-hidden text-[10px] shrink-0">
          {["month", "week", "day"].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`px-2 py-1 ${view === v ? "bg-teal-500 text-white" : "bg-white text-slate-600"}`}
            >
              {v === "month" ? "Ho" : v === "week" ? "Het" : "Nap"}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-xs text-slate-500 px-3 py-1 shrink-0">Betoltes...</div>}

      {view === "month" && (
        <div className="flex-1 overflow-y-auto p-2">
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {WEEKDAYS.map((w) => (
              <div key={w} className="text-center text-[10px] text-slate-500 font-medium">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {monthCells.map((d) => {
              const iso = isoLocal(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const dp = plansByDate[iso] || [];
              const selected = iso === selectedDate;
              const isToday = iso === new Date().toISOString().split("T")[0];
              return (
                <button
                  key={iso}
                  type="button"
                  onClick={() => {
                    onSelectDate(iso);
                    if (dp.length) onOpenMapForDate(iso);
                  }}
                  className={`min-h-[48px] rounded border p-0.5 text-left ${
                    selected ? "border-teal-500 bg-teal-50" : "border-slate-200 bg-white"
                  } ${!inMonth ? "opacity-35" : ""}`}
                >
                  <div className={`text-[11px] font-semibold w-5 h-5 flex items-center justify-center rounded-full ${
                    isToday ? "bg-teal-500 text-white" : "text-slate-700"
                  }`}>
                    {d.getDate()}
                  </div>
                  <div className="flex flex-wrap gap-0.5 mt-0.5">
                    {dp.slice(0, 2).map((p) => (
                      <span key={p.id} className="text-[8px] px-0.5 rounded bg-teal-600 text-white leading-tight">
                        {p.route_code || "?"}
                      </span>
                    ))}
                    {dp.length > 2 && <span className="text-[8px] text-slate-400">+{dp.length - 2}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <WeekTimeGrid
          weekDays={weekDays}
          plansByDate={plansByDate}
          selectedDate={selectedDate}
          onSelectDate={onSelectDate}
          onOpenMap={onOpenMapForDate}
          scrollKey={scrollKey}
        />
      )}

      {view === "day" && (
        <DayTimeGrid
          plans={dayPlans}
          onOpenMap={() => onOpenMapForDate(selectedDate)}
          scrollKey={scrollKey}
        />
      )}
    </div>
  );
};

export default MobileCleanerCalendar;
